import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type Team, type UnassignedUser } from '../../services/api';
import { toast } from '../shared/Toast';
import '../shared/TeamPicker';

interface Row {
  user: UnassignedUser;
  pickedTeamId: string;
  saving: boolean;
}

@customElement('unassigned-user-list')
export class UnassignedUserList extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state() private rows: Row[] = [];
  @state() private teams: Team[] = [];
  @state() private loading = true;
  @state() private searchQuery = '';

  async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
  }

  private async loadData() {
    this.loading = true;
    try {
      const [users, teams] = await Promise.all([
        api.getUnassignedUsers(),
        api.getTeams(),
      ]);
      this.teams = (teams || []).slice().sort((a, b) => a.name.localeCompare(b.name));
      this.rows = (users || []).map(u => ({ user: u, pickedTeamId: '', saving: false }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load unassigned users');
    } finally {
      this.loading = false;
    }
  }

  private async assign(index: number) {
    const row = this.rows[index];
    if (!row || !row.pickedTeamId || row.saving) return;

    this.rows = this.rows.map((r, i) => i === index ? { ...r, saving: true } : r);
    try {
      await api.assignUserToTeam(row.user.id, row.pickedTeamId);
      const teamName = this.teams.find(t => t.id === row.pickedTeamId)?.name ?? 'team';
      toast.success(`Assigned ${row.user.display_name || row.user.email} to ${teamName}`);
      // Drop the row from the list — they're no longer unassigned
      this.rows = this.rows.filter((_, i) => i !== index);
    } catch (err) {
      this.rows = this.rows.map((r, i) => i === index ? { ...r, saving: false } : r);
      toast.error(err instanceof Error ? err.message : 'Failed to assign user');
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex items-center gap-2 py-8 justify-center">
          <span class="loading loading-spinner loading-md"></span>
          <span class="text-base-content/70">Loading unassigned users...</span>
        </div>
      `;
    }

    if (this.rows.length === 0) {
      return html`
        <div class="alert alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>No unassigned users — every signed-up coach is on a team.</span>
        </div>
      `;
    }

    const q = this.searchQuery.trim().toLowerCase();
    const filtered = !q
      ? this.rows.map((row, index) => ({ row, index }))
      : this.rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) =>
            row.user.email.toLowerCase().includes(q) ||
            (row.user.display_name || '').toLowerCase().includes(q)
          );

    return html`
      <div class="space-y-4">
        <div>
          <h3 class="text-lg font-semibold">Unassigned Users</h3>
          <p class="text-sm text-base-content/70 mt-1">
            These users signed up but never joined a team — usually because they signed in with
            a different email than the one their invite was sent to. Pick a team for each one.
          </p>
        </div>

        <div class="flex items-center gap-2">
          <input
            type="text"
            class="input input-bordered input-sm flex-1"
            placeholder="Search by name or email..."
            .value=${this.searchQuery}
            @input=${(e: Event) => { this.searchQuery = (e.target as HTMLInputElement).value; }}
          />
          <span class="text-sm text-base-content/60 whitespace-nowrap">
            ${filtered.length} of ${this.rows.length}
          </span>
        </div>

        <!-- Desktop table -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Signed up</th>
                <th>Team</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(({ row, index }) => this.renderRow(row, index))}
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="sm:hidden space-y-2">
          ${filtered.map(({ row, index }) => this.renderCard(row, index))}
        </div>
      </div>
    `;
  }

  private renderRow(row: Row, index: number) {
    return html`
      <tr>
        <td class="font-medium">${row.user.display_name || html`<span class="text-base-content/40">—</span>`}</td>
        <td class="text-sm">${row.user.email}</td>
        <td class="text-xs text-base-content/60">
          ${new Date(row.user.created_at).toLocaleDateString()}
          ${row.user.got_invite ? html` <span class="badge badge-ghost badge-xs ml-1">invited</span>` : nothing}
        </td>
        <td>
          <div class="w-56">
            <team-picker
              .teams=${this.teams}
              .value=${row.pickedTeamId}
              placeholder="Pick a team…"
              @team-change=${(e: CustomEvent<{ id: string }>) => {
                const v = e.detail.id;
                this.rows = this.rows.map((r, i) => i === index ? { ...r, pickedTeamId: v } : r);
              }}
            ></team-picker>
          </div>
        </td>
        <td>
          <button
            class="btn btn-primary btn-xs"
            ?disabled=${!row.pickedTeamId || row.saving}
            @click=${() => this.assign(index)}
          >
            ${row.saving ? html`<span class="loading loading-spinner loading-xs"></span>` : 'Assign'}
          </button>
        </td>
      </tr>
    `;
  }

  private renderCard(row: Row, index: number) {
    return html`
      <div class="border rounded-lg p-3 bg-base-100 border-base-300">
        <div class="flex items-center justify-between gap-2">
          <span class="font-medium text-sm truncate">${row.user.display_name || row.user.email}</span>
          ${row.user.got_invite ? html`<span class="badge badge-ghost badge-xs shrink-0">invited</span>` : nothing}
        </div>
        ${row.user.display_name ? html`<div class="text-xs text-base-content/60 truncate mt-0.5">${row.user.email}</div>` : nothing}
        <div class="text-xs text-base-content/60 mt-1">
          Signed up ${new Date(row.user.created_at).toLocaleDateString()}
        </div>
        <div class="mt-2 flex items-center gap-2">
          <div class="flex-1 min-w-0">
            <team-picker
              .teams=${this.teams}
              .value=${row.pickedTeamId}
              placeholder="Pick a team…"
              @team-change=${(e: CustomEvent<{ id: string }>) => {
                const v = e.detail.id;
                this.rows = this.rows.map((r, i) => i === index ? { ...r, pickedTeamId: v } : r);
              }}
            ></team-picker>
          </div>
          <button
            class="btn btn-primary btn-sm shrink-0"
            ?disabled=${!row.pickedTeamId || row.saving}
            @click=${() => this.assign(index)}
          >
            ${row.saving ? html`<span class="loading loading-spinner loading-xs"></span>` : 'Assign'}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'unassigned-user-list': UnassignedUserList;
  }
}
